import { getInjector, getRootScope } from '@/services/angularjs-bridge';

import { BulkSearch, isBulkSearchInProgress } from './bulkSearch.types';

const STORAGE_KEY = 'BulkSearch';

type BulkSearchRootScope = {
    bulkSearch?: BulkSearch | false;
    bulkSearchLoading?: boolean;
    bulkSearchStillRunning?: boolean;
    $evalAsync?: (fn?: () => void) => void;
    $watch?: (expr: string, listener: (value: unknown) => void) => () => void;
    $on?: (name: string, listener: () => void) => () => void;
};

function getBulkSearchRootScope(): BulkSearchRootScope | null {
    try {
        return getRootScope(getInjector()) as BulkSearchRootScope;
    } catch {
        return null;
    }
}

export function readStoredBulkSearch(): BulkSearch | null {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as BulkSearch;
        return parsed?.id ? parsed : null;
    } catch {
        return null;
    }
}

/**
 * Mirror `bulk-search-controller.js#setCurrentBulkSearch`: persist to
 * localStorage and `$rootScope` so Catalog / last-results still work.
 */
export function persistCurrentBulkSearch(bulkSearch: BulkSearch): void {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(bulkSearch));

    const $rootScope = getBulkSearchRootScope();
    if (!$rootScope) {
        return;
    }

    $rootScope.bulkSearch = bulkSearch;
    $rootScope.bulkSearchLoading = isBulkSearchInProgress(bulkSearch);
    $rootScope.bulkSearchStillRunning = bulkSearch.status === 'RUNNING';
    $rootScope.$evalAsync?.();
}

/**
 * Angular broadcasts `fetchItems` when a bulk search moves to RUNNING or
 * READY. Use that as a prompt to re-fetch status (the READY payload often
 * omits `finished`, so `$rootScope.bulkSearchLoading` can stay true).
 */
export function subscribeBulkSearchUpdates(onUpdate: () => void): () => void {
    const $rootScope = getBulkSearchRootScope();
    if (!$rootScope?.$on) {
        return () => undefined;
    }

    const offFetch = $rootScope.$on('fetchItems', onUpdate);
    const unwatchRunning = $rootScope.$watch?.('bulkSearchStillRunning', () => {
        onUpdate();
    });

    return () => {
        offFetch?.();
        unwatchRunning?.();
    };
}
